import { colorSchemeDark, themeQuartz } from 'ag-grid-community'
import { DATA_GRID_TOKENS as T } from './tokens'

/**
 * The house AG Grid theme: Quartz + the dark colour scheme, repainted with our
 * own palette so a grid reads as part of the app rather than a widget dropped
 * into it. Every value comes from `tokens.ts`, which this theme shares with
 * grids that draw the same look without AG Grid.
 *
 * Two rules this file exists to enforce in ONE place:
 *
 * 1. `accentColor` is `colors.PRIMARY` — tenant-aware. `PRIMARY_300` is always
 *    the SportNinja yellow and would paint Canlan's sort arrows and focus rings
 *    the wrong brand colour.
 * 2. `fontFamily: 'inherit'` — the wrapper element carries `${font.body}`
 *    (Inter on SportNinja, Gilroy on Canlan) and the grid inherits it. The
 *    tenant font lives in an Emotion `SerializedStyles` block, which is an
 *    object, NOT a font-family string — passing it here would serialize to
 *    garbage.
 *
 * Contrast follows the app's antd tables (`Statistics`): a `SECONDARY_300`
 * header over rows that alternate `SECONDARY_300` / `SECONDARY_400`, separated
 * by hairlines rather than by thick page-coloured gutters. Banding is what
 * carries the eye across a 10-column sheet; the gutters this file used to draw
 * were a second, competing banding system.
 */
export const dataGridTheme = themeQuartz.withPart(colorSchemeDark).withParams({
  accentColor: T.accent,
  // The row surface, one step ABOVE the page.
  backgroundColor: T.background,
  foregroundColor: T.foreground,
  cellTextColor: T.text,
  borderColor: T.borderColor,
  browserColorScheme: 'dark',

  headerBackgroundColor: T.background,
  headerTextColor: T.headerText,
  // Quartz rules a vertical line between every header cell. On a 10-column
  // money sheet that reads as cage bars; the banded rows carry the structure.
  headerColumnBorder: false,
  columnBorder: false,
  // Hairlines, not gutters: the zebra below is the banding, and a 3px band of
  // page colour between every row on top of it reads as a broken table.
  headerRowBorder: {
    style: 'solid',
    width: T.borderWidth,
    color: T.rowBorderColor,
  },
  rowBorder: { style: 'solid', width: T.borderWidth, color: T.rowBorderColor },
  wrapperBorder: {
    style: 'solid',
    width: T.borderWidth,
    color: T.wrapperBorderColor,
  },
  // No frozen-pane seam, for the same reason as `columnBorder` above: a vertical
  // rule reads as a cage bar on a money sheet. Quartz draws one either side of a
  // pinned section by default, and in the page colour it lands as a black slab
  // beside PLAYER and ACTIONS — visible even when nothing is scrolled, which is
  // most of the time. The pinned columns simply sit flush with the rest.
  pinnedColumnBorder: false,

  // Zebra, matching the antd tables: first row on the base surface, the next one
  // step down. AG Grid counts from zero, so `.ag-row-odd` is the SECOND row —
  // the same order Statistics renders (`nth-child(odd)` is its first).
  oddRowBackgroundColor: T.oddRowBackground,
  rowHoverColor: T.rowHover,
  // Quartz's own values, set explicitly so the registration grid shares them
  // (see tokens.ts).
  selectedRowBackgroundColor: T.selectedRowBackground,
  focusShadow: T.focusShadow,
  popupShadow: T.popupShadow,

  // The pinned totals band reads as a footer: one step brighter again.
  pinnedRowBackgroundColor: T.pinnedRowBackground,
  pinnedRowTextColor: T.text,

  // Quartz's checkbox, set explicitly for the same reason — except the checked
  // tick, which follows the primary-fill content rule (tokens.ts).
  checkboxBorderRadius: T.checkbox.radius,
  checkboxUncheckedBackgroundColor: T.checkbox.uncheckedBackground,
  checkboxUncheckedBorderColor: T.checkbox.uncheckedBorder,
  checkboxCheckedBackgroundColor: T.checkbox.checkedBackground,
  checkboxCheckedBorderColor: T.checkbox.checkedBackground,
  checkboxCheckedShapeColor: T.checkbox.checkedShape,
  checkboxIndeterminateBackgroundColor: T.checkbox.indeterminateBackground,
  checkboxIndeterminateBorderColor: T.checkbox.indeterminateBackground,
  checkboxIndeterminateShapeColor: T.checkbox.indeterminateShape,

  fontFamily: 'inherit',
  fontSize: T.fontSize,
  wrapperBorderRadius: T.radius,
  cellHorizontalPadding: T.cellHorizontalPadding,
})

export default dataGridTheme
